import React, { useState, useEffect, useRef, useCallback } from "react";
import Page from "./pages/Page";
import ErrorBoundary from "./global/ErrorBoundary";
import { PageLoadingSpinner } from "./global/LoadingSpinner";
import Login from "./pages/Login";
import {
	updateLoginStatus,
	updateUserPermissions,
	updateUser,
	allowCollections,
	updateMobileState,
	setOrganization,
} from "../js/actions";
import { connect } from "react-redux";
import { withRouterHooks } from "../helpers/withRouterHooks";
import {
	flattenOrgTree,
	getStoredOrganizationName,
	persistOrganizationContext,
} from "../helpers/helpers";
import apiHandler from "../js/apiHandler";
import constants from "../js/constants";
import { signOut } from "aws-amplify/auth";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/bundle.less";

function getParameterByName(name, url) {
	if (!url) url = window.location.href;
	name = name.replace(/[\[\]]/g, "\\$&");
	const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)");
	const results = regex.exec(url);
	if (!results) return null;
	if (!results[2]) return "";
	return decodeURIComponent(results[2].replace(/\+/g, " "));
}

function App(props) {
	const {
		location,
		history,
		authState,
		isLoggedIn,
		updateLoginStatus,
		updateUserPermissions,
		updateUser,
		setOrganization,
		updateMobileState,
		allowCollections,
	} = props;

	const [isLoading, setIsLoading] = useState(false);
	const [permissionsResponse, setPermissionsResponse] = useState("");
	const [initialView, setInitialView] = useState("");
	const [firstName, setFirstName] = useState("");
	const [emailAddress, setEmailAddress] = useState("");

	/** Fetch user's roles and permissions, then initialize org context **/
	const getPermissions = useCallback(async () => {
		try {
			// This will throw an exception if the user is not authenticated
			await apiHandler.getAuthenticatedUser();

			let data = {};
			let getUserResponse;

			try {
				getUserResponse = await apiHandler.get(
					constants.ACCAPI.CURRENT_USER + "?view=simplified",
					{ apiName: constants.ACCAPI.NAME }
				);
				data = getUserResponse?.data || {};
			} catch (e) {
				// Retrying immediately re-sent the exact same request, which only ever
				// helps a pure network blip. The account-linking case this was meant for
				// (older users without sub_id) is more likely an eventual-consistency
				// race, so wait briefly before retrying. Errors are logged so failures
				// here are diagnosable instead of silently landing on the "no
				// organization roles" screen with no trace of why.
				await new Promise((resolve) => setTimeout(resolve, 750));
				try {
					getUserResponse = await apiHandler.get(
						constants.ACCAPI.CURRENT_USER + "?view=simplified",
						{ apiName: constants.ACCAPI.NAME }
					);
					data = getUserResponse?.data || {};
				} catch (retryError) {
					console.error(
						"getPermissions: /currentuser failed on both attempts",
						retryError
					);
				}
			}

			// Filter out internal orgs (ranative, rawebsite) from org trees (case-insensitive)
			const shouldExcludeOrg = (name) => {
				if (!name) return false;
				const lower = String(name).toLowerCase();
				return lower.includes("ranative") || lower.includes("rawebsite");
			};
			const filterOrgs = (arr = []) =>
				arr.filter((item) => !shouldExcludeOrg(item?.organization_name));

			data.organization_role_tree = filterOrgs(
				data?.organization_role_tree || []
			);

			const availableOrgs = flattenOrgTree(data.organization_role_tree || []);

			if (availableOrgs.length) {
				// Choose an initial organization (prefer stored org if present)
				let initOrg = {};

				const orgParam = getParameterByName("organization", location?.search);

				if (orgParam) {
					const found = availableOrgs.find(
						(o) => o.organization_name === orgParam
					);
					if (found) {
						initOrg = found;
						// Clear the query parameter
						const searchParams = new URLSearchParams(location.search);
						searchParams.delete("organization");
						const newSearch = searchParams.toString();
						history.replace(
							`${location.pathname}${newSearch ? `?${newSearch}` : ""}`
						);
					}
				}

				if (Object.keys(initOrg).length === 0) {
					const storedOrg = getStoredOrganizationName();
					if (storedOrg) {
						const found = availableOrgs.find(
							(o) => o.organization_name === storedOrg
						);
						if (found) initOrg = found;
					}
				}

				if (Object.keys(initOrg).length === 0) {
					initOrg = availableOrgs[0];
				}

				// Persist org context (localStorage so new tabs keep the selection)
				persistOrganizationContext({
					org: initOrg.organization_name,
					orgTitle: initOrg.title,
					orgId: initOrg.organization_id || "",
				});

				// Fetch permissions for selected org
				let permissions = [];
				try {
					const permissionResp = await apiHandler.get(
						`/currentuser/${initOrg.organization_name}/permissions`,
						{ apiName: "accapi" }
					);
					permissions = permissionResp?.data?.permissions || [];
				} catch (e) {
					console.log(e);
				}
				updateUserPermissions(permissions);

				// Update user and organization in store
				updateUser(data);
				setOrganization({
					org: initOrg.organization_name,
					orgTitle: initOrg.title,
					orgId: initOrg.organization_id,
				});

				if (data?.verification_status?.email_verified === false) {
					setInitialView("verifyAccount");
				}
			} else {
				// No organizations available
				setFirstName(data?.contact_info?.first_name || "");
				setEmailAddress(data?.contact_info?.email || "");
				setInitialView("noOrganizationRoles");
				if (getUserResponse) {
					setPermissionsResponse("ACCAPI " + String(getUserResponse));
				}
			}
			return data;
		} catch (e) {
			setPermissionsResponse("PERM EXP: " + String(e));
		}
	}, [location, history, updateUserPermissions, updateUser, setOrganization]);

	const toggleAuth = useCallback(
		async (val) => {
			setInitialView("");

			if (val) {
				await getPermissions();
			} else {
				await signOut();
			}

			updateLoginStatus(val);
		},
		[getPermissions, updateLoginStatus]
	);

	/** Fetch authenticated user if already logged-in **/
	const checkSession = useCallback(async () => {
		setIsLoading(true);
		try {
			await apiHandler.getAuthenticatedUser();
			await toggleAuth(true);
		} catch (err) {
			await toggleAuth(false);
		} finally {
			setIsLoading(false);
		}
	}, [toggleAuth]);

	const initApp = useCallback(async () => {
		// search = full token id in the url including /?token_id=.
		const { search } = location;

		const featureFlag =
			sessionStorage.getItem(constants.FEATURE_FLAG) ||
			getParameterByName(constants.FEATURE_FLAG);

		// if action=signup, sign out the user and render the create account page.
		const isSignUp = getParameterByName("action", search) === "signup";

		if (isSignUp) {
			await signOut();
			const email = getParameterByName("email", search);
			setEmailAddress(email);
			setInitialView("createAccount");
			updateLoginStatus(false);
		} else {
			setInitialView("");
			await checkSession();
		}
		if (
			featureFlag &&
			featureFlag.toLowerCase() === constants.FEATURE_COLLECTIONS
		) {
			allowCollections(true);
			sessionStorage.setItem(
				constants.FEATURE_FLAG,
				constants.FEATURE_COLLECTIONS
			);
		}
	}, [location, checkSession, updateLoginStatus, allowCollections]);

	const clearTokenId = useCallback(() => {}, []);

	// Mount: start listening for viewport size changes and kick off session init.
	useEffect(() => {
		const handleWindowResize = () => {
			updateMobileState(window.innerWidth < 768);
		};
		window.addEventListener("resize", handleWindowResize);
		handleWindowResize();
		initApp();

		return () => window.removeEventListener("resize", handleWindowResize);
		// Intentionally run once on mount only, mirroring the previous componentDidMount.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Re-fetch permissions when the URL's ?organization= param changes (e.g. an org-switch link).
	const prevLocationRef = useRef(location);
	useEffect(() => {
		const prevLocation = prevLocationRef.current;
		if (location !== prevLocation) {
			const orgParam = getParameterByName("organization", location.search);
			const prevOrgParam = getParameterByName(
				"organization",
				prevLocation.search
			);

			if (orgParam && orgParam !== prevOrgParam) {
				getPermissions();
			}
		}
		prevLocationRef.current = location;
	}, [location, getPermissions]);

	const isFullyAuthenticated =
		isLoggedIn &&
		authState?.permissions?.length > 0 &&
		authState?.user?.verification_status?.email_verified == true;

	const isEmailVerified =
		authState?.user?.verification_status?.email_verified == true;

	const isPhoneVerified =
		authState?.user?.verification_status?.phone_verified === true;

	return (
		<ErrorBoundary showDetails={process.env.NODE_ENV === "development"}>
			{isLoading ? (
				<PageLoadingSpinner message="Initializing application..." />
			) : isFullyAuthenticated ? (
				<Page onLogout={toggleAuth} />
			) : (
				<Login
					onLogin={updateLoginStatus}
					onLogout={toggleAuth}
					getPermissions={getPermissions}
					history={history}
					initialView={initialView || ""}
					firstName={firstName}
					email={emailAddress}
					isEmailVerified={isEmailVerified}
					isPhoneVerified={isPhoneVerified}
					clearTokenId={clearTokenId}
				/>
			)}
		</ErrorBoundary>
	);
}

const mapStateToProps = (state) => {
	return {
		authState: state.authState,
		isLoggedIn: state.authState ? state.authState.isLoggedIn : false,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		updateLoginStatus: (isLoggedIn) => {
			dispatch(updateLoginStatus(isLoggedIn));
		},
		updateUserPermissions: (perms) => {
			dispatch(updateUserPermissions(perms));
		},
		updateUser: (user) => {
			dispatch(updateUser(user));
		},
		setOrganization: (payload) => {
			dispatch(setOrganization(payload));
		},
		updateMobileState: (mobileMode) => {
			dispatch(updateMobileState(mobileMode));
		},
		allowCollections: (enable) => {
			dispatch(allowCollections(enable));
		},
	};
};

export default withRouterHooks(
	connect(mapStateToProps, mapDispatchToProps)(App)
);
